// Cookie categories
export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics'
};

// Cookie names
export const COOKIES = {
  CONSENT: 'cookie_consent',
  THEME: 'theme_preference',
  LANGUAGE: 'language_preference',
  AUTH_TOKEN: 'peart_auth_token',
  AUTH_EXPIRY: 'peart_auth_token_expiry'
};

// Cookie consent states
export const CONSENT_STATES = {
  ACCEPTED_ALL: 'accepted_all',
  ACCEPTED_SELECTED: 'accepted_selected',
  REJECTED: 'rejected',
  PENDING: 'pending'
};

class CookieService {
  constructor() {
    this.consent = this.loadConsent();
  }

  // Load consent from cookie
  loadConsent() {
    const consentCookie = this.getCookie(COOKIES.CONSENT);
    if (consentCookie) {
      try {
        return JSON.parse(consentCookie);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  // Save consent to cookie
  saveConsent(consentData) {
    this.consent = consentData;
    this.setCookie(COOKIES.CONSENT, JSON.stringify(consentData), 365);
  }

  // Check if category is allowed
  isCategoryAllowed(category) {
    if (!this.consent) return false;
    if (category === COOKIE_CATEGORIES.NECESSARY) return true;
    return this.consent.categories && this.consent.categories[category] === true;
  }

  // Check if we have any consent
  hasConsent() {
    return this.consent !== null;
  }

  // Get consent state
  getConsentState() {
    if (!this.consent) return CONSENT_STATES.PENDING;
    return this.consent.state || CONSENT_STATES.PENDING;
  }

  // Accept all cookies
  acceptAll() {
    const consentData = {
      state: CONSENT_STATES.ACCEPTED_ALL,
      timestamp: new Date().toISOString(),
      categories: {
        [COOKIE_CATEGORIES.NECESSARY]: true,
        [COOKIE_CATEGORIES.FUNCTIONAL]: true,
        [COOKIE_CATEGORIES.ANALYTICS]: true
      }
    };
    this.saveConsent(consentData);
  }

  // Accept selected categories
  acceptSelected(categories) {
    const consentData = {
      state: CONSENT_STATES.ACCEPTED_SELECTED,
      timestamp: new Date().toISOString(),
      categories: {
        [COOKIE_CATEGORIES.NECESSARY]: true,
        [COOKIE_CATEGORIES.FUNCTIONAL]: categories.functional || false,
        [COOKIE_CATEGORIES.ANALYTICS]: categories.analytics || false
      }
    };
    this.saveConsent(consentData);
  }

  // Reject all optional cookies
  rejectAll() {
    const consentData = {
      state: CONSENT_STATES.REJECTED,
      timestamp: new Date().toISOString(),
      categories: {
        [COOKIE_CATEGORIES.NECESSARY]: true,
        [COOKIE_CATEGORIES.FUNCTIONAL]: false,
        [COOKIE_CATEGORIES.ANALYTICS]: false
      }
    };
    this.saveConsent(consentData);
    // Remove existing functional/analytics cookies
    this.removeNonEssentialCookies();
  }

  // Remove non-essential cookies and localStorage items
  removeNonEssentialCookies() {
    // Remove theme from localStorage if functional cookies not allowed
    if (!this.isCategoryAllowed(COOKIE_CATEGORIES.FUNCTIONAL)) {
      localStorage.removeItem('theme');
      // Remove language preference cookie
      document.cookie = 'i18next=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
  }

  // Helper: Set cookie
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  // Helper: Get cookie
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Helper: Delete cookie
  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
}

export default new CookieService();